import { FC } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { findApiErrorMessage, isConflictError } from "@api/apiError";
import { useDeclineOrgBillingTransferMutation } from "@api/orgBillingTransferRecipient.api";
import { DECLINE_REASON_MAX_LENGTH } from "@common/schemas/domains/billing/org-billing-transfer-request.schema";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { PendingOrgBillingTransferModalProps } from "./usePendingOrgBillingTransferModal";

const declineFormSchema = z.object({
    reason: z.string().trim().max(DECLINE_REASON_MAX_LENGTH).optional(),
});

type DeclineFormValues = z.infer<typeof declineFormSchema>;

const fields = createFormikFields<DeclineFormValues>();

export const DeclineOrgBillingTransferModal: FC<
    PendingOrgBillingTransferModalProps
> = ({ request, onClose }) => {
    const { success, error: showError } = useToaster();
    const decline = useDeclineOrgBillingTransferMutation();

    const formik = useFormik<DeclineFormValues>({
        initialValues: { reason: "" },
        validate: validateFormikWithZodSchema(declineFormSchema),
        onSubmit: submitHelper({
            handler: async ({ reason }) => {
                await decline.mutateAsync({ requestId: request.id, reason });
                success("Declined billing transfer request");
                onClose();
            },
            failureHandler: async (error) => {
                if (isConflictError(error)) {
                    onClose();
                    return;
                }

                showError(
                    findApiErrorMessage(error) ??
                        "Failed to decline billing transfer request",
                );
            },
        }),
    });

    return (
        <StandardModal show backdrop="static" onHide={onClose}>
            <StandardModalHeader>Decline billing transfer</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <Form>
                        <VStack gap={16}>
                            <p>
                                Decline the request from{" "}
                                {request.organizationName} to take over billing
                                for this workspace.
                            </p>
                            <fields.TextArea
                                name="reason"
                                label="Reason"
                                optional
                                size="xl"
                                rows={4}
                            />
                            <HStack as="footer" justify="end" gap={12}>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    onClick={onClose}
                                    disabled={formik.isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    type="submit"
                                    disabled={!formik.isValid}
                                    loading={formik.isSubmitting}
                                >
                                    Decline request
                                </ButtonWithLoader>
                            </HStack>
                        </VStack>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </StandardModal>
    );
};
